import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { AppSettings } from '../../config/appConfigs/appSettings';
import { throwUnlessDefined } from '../../utils/functional/functionalUtils';
import mapGeoJson from "./map/map.geojson";

const { mapboxStyle } = AppSettings;
const MAPBOX_TOKEN = throwUnlessDefined(process.env.REACT_MAPBOX_STYLE_LIGHT);
const MAPBOX_TOKEN_DARK_LIGHT = throwUnlessDefined(process.env.REACT_MAPBOX_TOKEN_FOR_DARK_LIGHT);
mapboxgl.accessToken = MAPBOX_TOKEN_DARK_LIGHT;

type Coordinate = [number, number];

const subdivideSegment = (p1: Coordinate, p2: Coordinate, subdivisions: number, spacing = 0.0005) => {
  const segments = [];
  for (let i = 0; i < subdivisions; i++) {
    const t1 = i / subdivisions;
    const t2 = (i + 1) / subdivisions;
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];

    const shrinkFactor = spacing / Math.hypot(dx, dy);
    const st1 = t1 + shrinkFactor / 2;
    const st2 = t2 - shrinkFactor / 2;

    const start = [p1[0] + dx * st1, p1[1] + dy * st1];
    const end = [p1[0] + dx * st2, p1[1] + dy * st2];
    segments.push([start, end]);
  }
  return segments;
};

function createExtrudedCirclePolygon(center: Coordinate, radius: number, points = 16) {
  const coordinates: Coordinate[] = [];
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = radius * Math.cos(angle);
    const dy = radius * Math.sin(angle);
    coordinates.push([center[0] + dx, center[1] + dy]);
  }
  coordinates.push(coordinates[0]);
  return {
    type: 'Polygon',
    coordinates: [coordinates]
  };
}

const SandUsageSubdividedTrackMap = ({
  track = [
    [-0.6823966708922455, 51.40378598563581],
    [-0.6825807270810174, 51.40291698070092],
    [-0.6827149395991228, 51.401409867864515],
    [-0.6824995036832036, 51.399167678001646],
    [-0.6821696119979492, 51.39789557030812],
    [-0.6818356481691126, 51.39701554788215],
    [-0.6805928253496063, 51.39374054710538],
    [-0.6795637196943858, 51.39109639410589],
    [-0.679443674741151, 51.3888387890828],
    [-0.6805316431830875, 51.38485359698706],
    [-0.6834853216369083, 51.37745420103073],
    [-0.686255863166565, 51.37026188818169]
  ],
  barWidth = 0.0001
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const selectedSegments = new Set([1, 3, 3]);

  const barFeatures = track.slice(0, -1).flatMap((p1, segIndex) => {
    if (!selectedSegments.has(segIndex)) return [];
    const p2 = track[segIndex + 1];
    const subSegments = subdivideSegment(p1, p2, 8, 0.0008);

    return subSegments.map(([subP1, subP2], subIndex) => {
      const activationTime = Math.floor(Math.random() * 30 + 10);
      const flowRate = (Math.random() * 0.5 + 2.5).toFixed(2);
      const totalSandUsed = ((activationTime / 60) * parseFloat(flowRate)).toFixed(2);
      const activationRate = Math.floor((activationTime / 40) * 100);
      const height = (activationRate / 100) * 700;
      const center: Coordinate = [(subP1[0] + subP2[0]) / 2, (subP1[1] + subP2[1]) / 2];

      return {
        type: 'Feature',
        geometry: createExtrudedCirclePolygon(center, barWidth * 0.5),
        properties: {
          activationTime,
          flowRate,
          totalSandUsed,
          activationRate,
          height,
          segment: segIndex + 1,
          subdivision: subIndex + 1,
          featureType: Math.random() < 0.5 ? 'greenFeature' : 'yellowFeature'
        }
      };
    });
  });

  useEffect(() => {
    const resizeTimeout = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.resize(); // Fix blank map on hidden DOM containers
      }
    }, 300); // Delay allows DOM to settle
  console.log(
    " we are in map"
  )
    return () => clearTimeout(resizeTimeout);
  }, []);
  
  useEffect(() => {
    if (mapContainerRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: process.env.REACT_MAPBOX_STYLE_LIGHT,
        center: track[Math.floor(track.length / 2)],
        zoom: 13.45,
        pitch: 70,
        bearing: -88,
        antialias: true
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      mapRef.current.on('load', () => {
        mapRef.current?.addSource('track', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: track }
          }
        });

        mapRef.current?.addLayer({
          id: 'track-line',
          type: 'line',
          source: 'track',
          paint: {
            'line-color': '#888',
            'line-width': 4
          }
        });

        mapRef.current?.addSource('sandUsageBars', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: barFeatures }
        });

        mapRef.current?.addLayer({
          id: 'sandUsage-bars',
          type: 'fill-extrusion',
          source: 'sandUsageBars',
          paint: {
          'fill-extrusion-color': [
  'case',
  ['>', ['get', 'activationRate'], 70], '#FB787F', // red
  '#3EB05E' // green
],
            'fill-extrusion-height': ['get', 'height'],
            'fill-extrusion-base': 10,
            'fill-extrusion-opacity': 0.95
          }
        });

        mapRef.current?.addLayer({
          id: 'sandUsage-bars-base',
          type: 'fill-extrusion',
          source: 'sandUsageBars',
          paint: {
            'fill-extrusion-color': [
              'case',
              ['==', ['get', 'featureType'], 'greenFeature'], '#00FF00',
              ['==', ['get', 'featureType'], 'yellowFeature'], '#FFFF00',
              '#FFFFFF'
            ],
            'fill-extrusion-height': 10,
            'fill-extrusion-opacity': 0.9
          }
        });
      });
    }
    return () => mapRef.current?.remove();
  }, [barFeatures, track]);

  return <div ref={mapContainerRef} style={{ width: '100%', height: '500px' }} />;
};

export default SandUsageSubdividedTrackMap;
